import React, { JSX } from 'react';
import { Box } from '@mui/material';
import PropagateLoader from 'react-spinners/PropagateLoader';
import { CandidateInfo } from 'components/ui/CandidateInfo';
import { Message } from 'components/Message';
import { Scrollable } from 'components/Scrollable';
import {
  Candidate,
  ChatMessage,
  ChatMessageError,
  ChatMessageStatus,
  ChatSession,
} from 'types/types';

const mobileMessageSx = {
  borderRadius: '10px',
  fontSize: '0.82rem',
  lineHeight: 1.28,
  mt: 0.5,
  padding: '7px 9px',
  '& .MuiSvgIcon-root': { fontSize: '1rem' },
};

function CandidateHeader({
  selectedCandidate,
  isMobile,
}: {
  selectedCandidate: Candidate;
  isMobile: boolean;
}): JSX.Element {
  return (
    <CandidateInfo
      key={selectedCandidate.username}
      action={`Chat with Backstory about ${selectedCandidate.firstName}`}
      elevation={4}
      candidate={selectedCandidate}
      variant={isMobile ? 'minimal' : 'normal'}
      sx={{
        width: '100%',
        flexGrow: 0,
        flexShrink: 0,
        height: 'auto',
        minHeight: 'min-content',
        maxHeight: isMobile ? 'none' : 0,
        px: isMobile ? 1.25 : undefined,
        py: isMobile ? 0.75 : undefined,
        borderBottom: isMobile ? '1px solid rgba(0, 0, 0, 0.08)' : undefined,
        '& .MuiAvatar-root': isMobile ? { width: 34, height: 34 } : undefined,
        '& .MuiTypography-body1': isMobile ? { fontSize: '0.88rem', lineHeight: 1.15 } : undefined,
      }}
    />
  );
}

interface ChatMessageListProps {
  chatSession: ChatSession | null;
  messages: ChatMessage[];
  processingMessage: ChatMessage | ChatMessageError | ChatMessageStatus | null;
  streamingMessage: ChatMessage | null;
  streaming: boolean;
  isMobile: boolean;
  welcomeMessage: ChatMessage;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
}

function ChatMessageList(props: ChatMessageListProps): JSX.Element {
  const messageSx = props.isMobile ? mobileMessageSx : undefined;
  return (
    <Scrollable
      sx={{
        width: '100%',
        flexBasis: 0,
        flexShrink: 1,
        alignItems: 'stretch',
        px: props.isMobile ? 0.75 : 0,
        py: props.isMobile ? 0.5 : 0,
        gap: props.isMobile ? 0.5 : 0,
      }}
    >
      {props.messages.length === 0 && <EmptyChatIntro {...props} messageSx={messageSx} />}
      {props.messages.map(message => (
        <Message
          key={message.id}
          sx={messageSx}
          chatSession={props.chatSession ?? undefined}
          message={message}
        />
      ))}
      {props.processingMessage !== null && (
        <Message
          sx={messageSx}
          chatSession={props.chatSession ?? undefined}
          message={props.processingMessage}
        />
      )}
      {props.streamingMessage !== null && (
        <Message
          sx={messageSx}
          chatSession={props.chatSession ?? undefined}
          message={props.streamingMessage}
        />
      )}
      {(props.streaming || !props.chatSession) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            m: 1,
          }}
        >
          <PropagateLoader
            size="10px"
            loading={props.streaming || !props.chatSession}
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        </Box>
      )}
      <div ref={props.messagesEndRef} />
    </Scrollable>
  );
}

function EmptyChatIntro({
  isMobile,
  welcomeMessage,
  chatSession,
  messageSx,
}: ChatMessageListProps & { messageSx: typeof mobileMessageSx | undefined }): JSX.Element {
  if (!isMobile) {
    return (
      <Message sx={messageSx} chatSession={chatSession ?? undefined} message={welcomeMessage} />
    );
  }
  return (
    <Box
      sx={{
        alignSelf: 'stretch',
        flexShrink: 0,
        mt: 0.75,
        p: '8px 10px',
        border: '1px solid rgba(74, 122, 125, 0.45)',
        borderRadius: '10px',
        backgroundColor: '#BFD8D8',
        color: 'text.primary',
        fontSize: '0.78rem',
        lineHeight: 1.3,
        whiteSpace: 'pre-line',
      }}
    >
      {welcomeMessage.content}
    </Box>
  );
}

export { CandidateHeader, ChatMessageList };
